// timeout dialog based off https://github.com/hmrc/hmrc-frontend/tree/main/src/components/timeout-dialog
/* eslint-disable no-use-before-define */
/**
 * TimeoutDialog
 *
 * Displays a GOV.UK-styled modal warning the user that their session is about
 * to expire, giving them the option to stay signed in or sign out immediately.
 *
 * Usage
 * -----
 * The dialog is driven entirely by `data-*` attributes on a trigger element
 * (typically a `<meta>` tag placed in the `<head>`):
 *
 * ```html
 * <meta
 *   name="timeout-dialog"
 *   data-timeout="900"
 *   data-countdown="120"
 *   data-keep-alive-url="/keep-alive"
 *   data-sign-out-url="/sign-out"
 * />
 * ```
 *
 * Required attributes
 * -------------------
 * | Attribute            | Type    | Description                                        |
 * |----------------------|---------|----------------------------------------------------|
 * | data-timeout         | integer | Total session length in seconds.                   |
 * | data-countdown       | integer | Seconds before expiry at which to show the dialog. |
 * | data-keep-alive-url  | string  | URL polled (GET) when the user chooses to stay in. |
 * | data-sign-out-url    | string  | URL the "Sign out" link points to.                 |
 *
 * Optional attributes
 * -------------------
 * | Attribute                   | Type    | Default (en)                              |
 * |-----------------------------|---------|-------------------------------------------|
 * | data-timeout-url            | string  | Falls back to data-sign-out-url           |
 * | data-language               | string  | Defaults to English; pass "cy" for Welsh  |
 * | data-title                  | string  | "You're about to be signed out"           |
 * | data-message                | string  | "For your security, we will sign you out in" |
 * | data-message-suffix         | string  | (empty)                                   |
 * | data-keep-alive-button-text | string  | "Stay signed in"                          |
 * | data-sign-out-button-text   | string  | "Sign out"                                |
 * | data-synchronise-tabs       | boolean | false                                     |
 * | data-hide-sign-out-button   | boolean | false                                     |
 */

import keepAlive from './keepAlive';
import displayDialog from './displayDialog';
import SessionActivityService from './SessionActivityService';

/** English (default) localised strings. When DATA translations not passed */
const EN_DEFAULTS = {
    title: "You're about to be signed out",
    keepAliveButtonText: 'Stay signed in',
    signOutButtonText: 'Sign out',
    message: 'For your security, we will sign you out in',
    secondText: 'second.',
    secondsText: 'seconds.',
    minuteText: 'minute.',
    minutesText: 'minutes.',
};

/** Welsh localised strings. When DATA translations not passed */
const CY_DEFAULTS = {
    title: 'Rydych ar fin cael eich allgofnodi',
    keepAliveButtonText: "Parhau i fod wedi'ch mewngofnodi",
    signOutButtonText: 'Allgofnodi',
    message: 'Er eich diogelwch, byddwn yn eich allgofnodi cyn pen',
    secondText: 'eiliad.',
    secondsText: 'eiliad.',
    minuteText: 'munud.',
    minutesText: 'munud.',
};

/**
 * @param {Element} $module The trigger element that carries the `data-*` configuration attributes.
 * @param {SessionActivityService} $sessionActivityService broadcast and listen for keep-alive events across tabs.
 * @returns {{ init: () => void, cleanup: () => void }}
 */
function TimeoutDialog($module, $sessionActivityService) {
    /** Parsed & validated option values (before merging with defaults). */
    let options = {};

    /** Fully merged runtime settings (options + localised defaults). */
    let settings = {};

    /**
     * Teardown functions collected during setup.  Each call to `cleanup()`
     * drains this array – clearing timers, removing event listeners, etc.
     *
     * @type {Array<() => void>}
     */
    const cleanupFunctions = [];

    /**
     * Handle for the repeating countdown-update timer rendered inside the
     * dialog.  Stored separately so it can be cancelled when the dialog
     * closes.
     *
     * @type {number|undefined}
     */
    let currentTimer;

    /**
     * The most recent countdown value announced to screen readers, used to
     * reduce the frequency of updates when more than a minute remains by only
     * announcing every whole-minute change (e.g. "2 minutes", "1 minute", "59 seconds").
     */
    let lastAnnouncedCounter;

    /**
     * Service for synchronising session activity across multiple tabs.
     * @type {SessionActivityService}
     */
    const sessionActivityService = $sessionActivityService;

    /** Redirect the user to the manual sign-out URL. */
    const signOut = () => {
        window.location.href = settings.signOutUrl;
    };

    /** Redirect the user to the automatic timeout URL (may differ from sign-out URL). */
    const onTimeout = () => {
        window.location.href = settings.timeoutUrl;
    };

    // ── Validation & defaults ───────────────────────────────────────────────

    /**
     * Assert that all required configuration keys are present and non-empty.
     *
     * @param {object} config - The parsed options object.
     * @throws {Error} When one or more required keys are missing.
     */
    const validateRequiredOptions = (config) => {
        const required = ['timeout', 'countdown', 'keepAliveUrl', 'signOutUrl'];
        const missing = required.filter((key) => !config[key]);

        if (missing.length > 0) {
            const attrNames = missing.map((k) => `data-${k.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()}`);
            throw new Error(`Missing config item(s): [${attrNames.join(', ')}]`);
        }
    };

    // ── Timer management ────────────────────────────────────────────────────

    /**
     * Schedule the dialog to appear after the appropriate delay.
     *
     * The dialog is shown `countdown` seconds before the session expires.
     * When `timeOfLastActivity` is supplied (e.g. from a cross-tab broadcast),
     * the delay is adjusted so that time already elapsed is subtracted.
     *
     * @param {number} [timeOfLastActivity=Date.now()]
     *   Unix timestamp (ms) of the most recent session activity.
     */
    const setupDialogTimer = (timeOfLastActivity = Date.now()) => {
        const signoutTime = timeOfLastActivity + settings.timeout * 1000;
        const elapsed = Date.now() - timeOfLastActivity;
        const secondsUntilDialog = settings.timeout - settings.countdown;
        const delayMs = secondsUntilDialog * 1000 - elapsed;

        if (delayMs <= 0) {
            // show immediately when should be showing
            // eslint-disable-next-line no-use-before-define
            setupDialog(signoutTime);
            // Still register a cleanup so the countdown tick timer (currentTimer) is
            // cancelled if the user acts before the first runUpdate fires.
            cleanupFunctions.push(() => {
                if (currentTimer) {
                    window.clearTimeout(currentTimer);
                }
            });
        } else {
            const timerId = window.setTimeout(() => {
                setupDialog(signoutTime);
            }, delayMs);

            cleanupFunctions.push(() => {
                window.clearTimeout(timerId);
                if (currentTimer) {
                    window.clearTimeout(currentTimer);
                }
            });
        }
    };
    /**
     * Initialise the timeout dialog.
     *
     * Reads configuration from `$module`'s `data-*` attributes, validates
     * required values, merges with localised defaults, then starts the timer
     * that will eventually show the dialog.
     */
    function init() {
        /**
         * Read a single `data-*` attribute value from the trigger element.
         *
         * @param {string} key - Full attribute name, e.g. `"data-timeout"`.
         * @returns {string|undefined}
         */
        function lookupData(key) {
            return ($module.attributes.getNamedItem(key) || {}).value;
        }

        const locale = lookupData('data-language') === 'cy' ? CY_DEFAULTS : EN_DEFAULTS;

        options = {
            timeout: parseInt(lookupData('data-timeout'), 10) || undefined,
            countdown: parseInt(lookupData('data-countdown'), 10) || undefined,
            keepAliveUrl: lookupData('data-keep-alive-url'),
            signOutUrl: lookupData('data-sign-out-url'),
            timeoutUrl: lookupData('data-timeout-url'),
            title: lookupData('data-title'),
            message: lookupData('data-message'),
            messageSuffix: lookupData('data-message-suffix'),
            keepAliveButtonText: lookupData('data-keep-alive-button-text'),
            signOutButtonText: lookupData('data-sign-out-button-text'),
            synchroniseTabs: lookupData('data-synchronise-tabs')?.toString() === 'true',
            hideSignOutButton: lookupData('data-hide-sign-out-button')?.toString() === 'true',
        };

        // Default timeoutUrl to signOutUrl when not explicitly provided.
        options.timeoutUrl = options.timeoutUrl || options.signOutUrl;
        options.locale = locale;

        // add default locale strings for any missing values
        validateRequiredOptions(options);
        settings = options;
        setupDialogTimer();
        listenForCrossTabActivity();
    }

    /**
     * Tear down the dialog and all associated timers / event listeners.
     * Safe to call multiple times.
     */
    const cleanup = () => {
        while (cleanupFunctions.length > 0) {
            cleanupFunctions.shift()();
        }
    };

    // ── Cross-tab synchronisation ───────────────────────────────────────────

    /**
     * If `synchroniseTabs` is enabled, listen for keep-alive events broadcast
     * by other tabs and reset the local countdown accordingly.
     */
    const listenForCrossTabActivity = () => {
        if (settings.synchroniseTabs) {
            sessionActivityService.onActivity((event) => {
                cleanup();
                setupDialogTimer(event.timestamp);
            });
        }
    };

    /**
     * Broadcast a session-activity event to all other open tabs.
     * Called after the user clicks "Stay signed in".
     */
    const broadcastSessionActivity = () => {
        sessionActivityService.logActivity();
    };

    // ── Dialog rendering ────────────────────────────────────────────────────

    /**
     * Build and display the timeout warning dialog.
     *
     * @param {number} signoutTime - Unix timestamp (ms) at which the session
     *   will expire and the user will be redirected automatically.
     */
    const setupDialog = (signoutTime) => {
        // as we are inserting data in JS from "data" fields we want to avoid XSS
        // by injecting html safe into premade HTML
        const blankHtml = ` 
            <div>
                <h1 id="timeout-heading" class="govuk-heading-m push--top"> </h1>
                <p id="timeout-message" class="govuk-body timeout-dialog__message" aria-hidden="true"></p>
                <p id="audible-timeout-message" class="govuk-visually-hidden screenreader-content"
                 aria-live="assertive"></p>
                <div class="govuk-button-group">
                    <button id="timeout-keep-signin-btn" class="govuk-button" type="button"></button>
                    <a id="timeout-sign-out-link" class="govuk-link"> </a>                    
                </div>
            </div>
        `;

        // Mount the dialog — displayDialog parses the HTML string internally.
        const dialogControl = displayDialog(blankHtml);

        cleanupFunctions.push(() => dialogControl.closeDialog());
        document.getElementById('timeout-keep-signin-btn').addEventListener('click', keepAliveAndClose);

        // Dismissing via Escape should behave the same as "Stay signed in".
        let escapeHandled = false;
        dialogControl.addCloseHandler(() => {
            if (!escapeHandled) {
                escapeHandled = true;
                keepAliveAndClose();
            }
        });

        // Populate text content after mounting to avoid any XSS risk from
        // user-supplied strings (data-* attribute values) being injected as raw HTML.

        const $signOutLink = document.getElementById('timeout-sign-out-link');
        if ($signOutLink) {
            if (!settings.hideSignOutButton) {
                $signOutLink.textContent = settings.signOutButtonText || settings.locale.signOutButtonText;
                $signOutLink.setAttribute('href', settings.signOutUrl);
                $signOutLink.addEventListener('click', signOut);
            } else {
                $signOutLink.style.display = 'none';
            }
        }

        const $countdownMessageElement = document.getElementById('timeout-message');
        $countdownMessageElement.textContent = `${settings.message || settings.locale.message} `;

        const countdownSpan = document.createElement('span');
        countdownSpan.setAttribute('id', 'timeout-counter');
        countdownSpan.setAttribute('class', 'timeout-dialog__countdown');

        $countdownMessageElement.appendChild(countdownSpan);

        // Grab the live countdown elements for use by the tick loop below.
        const $countdownElement = document.getElementById('timeout-counter');

        const $audibleMessage = document.getElementById('audible-timeout-message');

        // Set the static text after mounting to avoid XSS risk.
        const $headingElement = document.getElementById('timeout-heading');
        $headingElement.textContent = settings.title || settings.locale.title;

        const $keepAliveButton = document.getElementById('timeout-keep-signin-btn');
        $keepAliveButton.textContent = settings.keepAliveButtonText || settings.locale.keepAliveButtonText;

        // Associate the dialog with its heading and live region for screen readers
        dialogControl.setAriaLabelledBy('timeout-heading audible-timeout-message');

        const getMillisecondsRemaining = (now) => signoutTime - now;

        const getSecondsRemaining = (now) => Math.floor(getMillisecondsRemaining(now) / 1000);

        /**
         * Format a remaining-seconds value as human-readable text using the
         * active locale's template strings.
         *
         * @param {number} count - Remaining seconds (≥ 0).
         * @returns {string} e.g. "30 seconds." (en) or "2 funud" (cy)
         */
        const getHumanCounterText = (count) => {
            let newCount = count;
            let postCounterText = settings.locale.secondsText || 'second.';
            if (newCount === 1) {
                postCounterText = settings.locale.secondText || 'second.';
            } else if (newCount === 60) {
                postCounterText = settings.locale.minuteText || 'minute.';
                newCount = 1;
            } else if (newCount > 60) {
                postCounterText = settings.locale.minutesText || 'minutes.';
                newCount = Math.ceil(count / 60);
            }

            return `${newCount} ${postCounterText}`;
        };

        $countdownElement.textContent = getHumanCounterText(settings.countdown);

        /**
         * Build the full sentence read aloud by screen readers.
         *
         * reduce number of changes to every minute, every 20 seconds and then final 5 seconds
         * @param {number} counter - Remaining seconds.
         * @returns {string}
         */
        const getAudibleText = (counter) => {
            if (counter > 60) {
                // avoid spamming screen readers with minor updates if more than minute
                lastAnnouncedCounter = lastAnnouncedCounter || counter;
                if (counter - lastAnnouncedCounter >= 60) {
                    lastAnnouncedCounter = counter;
                    return getHumanCounterText(counter);
                }
                return getHumanCounterText(lastAnnouncedCounter);
            }
            if (counter > 20) {
                // 20 second counter avoid spamming screen readers with minor updates
                lastAnnouncedCounter = lastAnnouncedCounter || counter;
                if (counter - lastAnnouncedCounter >= 20) {
                    lastAnnouncedCounter = counter;
                    return getHumanCounterText(counter);
                }
                return getHumanCounterText(lastAnnouncedCounter);
            }
            // finally only announce last 5 seconds
            if (counter >= 5) {
                lastAnnouncedCounter = lastAnnouncedCounter || counter;
                if (counter - lastAnnouncedCounter >= 5) {
                    lastAnnouncedCounter = counter;
                    return getHumanCounterText(counter);
                }
                return getHumanCounterText(lastAnnouncedCounter);
            }

            return null;
        };

        /**
         * Only mutate `$el.innerText` when the text has actually changed,
         * avoiding unnecessary DOM thrashing and spurious AT announcements.
         *
         * @param {Element} $el
         * @param {string}  text
         */
        const updateTextIfChanged = ($el, text) => {
            if (text === null) {
                return;
            }

            if ($el.innerText !== text) {
                // eslint-disable-next-line no-param-reassign
                $el.innerText = text;
            }
        };

        /**
         * Refresh both the visual countdown span and the audible live region.
         *
         * @param {number} counter - Remaining seconds.
         */
        const updateCountdown = (counter) => {
            updateTextIfChanged($countdownElement, getHumanCounterText(counter));
            updateTextIfChanged($audibleMessage, getAudibleText(counter));
        };

        /**
         * Calculate the delay (ms) until the next countdown tick.
         *
         * Accepts the already-snapshotted `now` value from `runUpdate` so that
         * `Date.now()` is called exactly once per tick cycle.
         *
         * When more than 60 s remain, ticks fire on whole-minute boundaries to
         * reduce DOM mutations.  Within the final minute, ticks fire every
         * whole second.
         *
         * @param {number} now - The `Date.now()` snapshot taken at the start of this tick.
         * @returns {number}
         */
        const getNextTimeout = (now) => {
            const remaining = getMillisecondsRemaining(now);
            const roundedRemaining = Math.floor(remaining / 1000) * 1000;

            if (roundedRemaining < 60000) {
                // Final minute: tick every second.
                // If remaining is already on a whole-second boundary, wait exactly 1 s.
                return remaining - roundedRemaining || 1000;
            }

            // More than 60 s remain: tick on the next whole-minute boundary.
            const msIntoCurrentMinute = roundedRemaining % 60000; // 0 when exactly on a boundary
            const msUntilNextMinuteBoundary = msIntoCurrentMinute === 0 ? 60000 : msIntoCurrentMinute;
            return remaining - (roundedRemaining - msUntilNextMinuteBoundary);
        };

        /**
         * Main countdown loop.  Updates the UI on each tick, then either
         * redirects the user (when the counter reaches 0) or schedules the
         * next update.
         *
         * `Date.now()` is snapshotted once per tick and passed to all helpers
         * so the clock reading is consistent within a single update cycle.
         */
        const runUpdate = () => {
            const now = Date.now();
            const counter = Math.max(getSecondsRemaining(now), 0);
            updateCountdown(counter);

            if (counter <= 0) {
                onTimeout();
            } else {
                currentTimer = window.setTimeout(runUpdate, getNextTimeout(now));
            }
        };

        runUpdate();
    };

    /**
     * AbortController for any in-flight keep-alive fetch request.
     * Stored so a second invocation of keepAliveAndClose can cancel the
     * previous request rather than leaving an orphaned Promise in flight.
     *
     * @type {AbortController|undefined}
     */
    let keepAliveController;

    /**
     * Handle the user clicking "Stay signed in" (or pressing Escape):
     *  1. Tears down the current dialog and its timers.
     *  2. Restarts the idle timer from now.
     *  3. Pings the keep-alive endpoint so the server extends the session.
     *  4. Broadcasts the activity to any other open tabs.
     */
    const keepAliveAndClose = () => {
        cleanup();
        setupDialogTimer();

        // Abort any previous in-flight keep-alive request before starting a new one.
        if (keepAliveController) {
            keepAliveController.abort();
        }
        keepAliveController = new AbortController();

        // Explicitly handle the returned Promise so it is never silently ignored.
        keepAlive(settings.keepAliveUrl, keepAliveController.signal).catch(() => {
            // fetchGet already swallows errors internally; this catch guards
            // against any unexpected rejection escaping to an unhandledrejection
            // event on the window.
        });

        broadcastSessionActivity();
    };

    /** init should be called to initiate the timeout dialog */
    return { init, cleanup };
}

/** Initialise the timeout dialog when the page loads, if a trigger element is present. */
export default function initTimeoutDialog() {
    const $TimeoutDialog = document.querySelector('meta[name="timeout-dialog"]');
    if ($TimeoutDialog) {
        try {
            const sessionActivityService = new SessionActivityService(window.BroadcastChannel);
            new TimeoutDialog($TimeoutDialog, sessionActivityService).init();
        } catch (error) {
            // ignore failure
        }
    }
}

/* eslint-enable no-use-before-define */
